import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import api from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import PatientMeasurementTrendsOverlay from "../components/results/PatientMeasurementTrendsOverlay";
import PatientRecordingOverlay from "../components/results/PatientRecordingOverlay";
import { useDateTimeFormat } from "../hooks/useDateTimeFormat";
import { buildFunctionUrl } from "../utils/functionUrls";
import {
  readStoredSelectedPatient,
  writeStoredSelectedPatient,
} from "../utils/patientProfileSelection";
import { isElectronRxProfileEmail } from "../utils/patientProfileEmail";

const statusClassNames = {
  pass: "bg-green-100 text-green-800 border-green-200",
  good: "bg-green-100 text-green-800 border-green-200",
  "very good": "bg-green-100 text-green-800 border-green-200",
  fail: "bg-red-100 text-red-800 border-red-200",
  bad: "bg-red-100 text-red-800 border-red-200",
  unknown: "bg-greyscale-1 text-typography border-greyscale-1",
};

function normaliseStatus(value) {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    if (
      normalized === "pass" ||
      normalized === "passed" ||
      normalized === "good" ||
      normalized === "very good"
    ) {
      return normalized === "passed" ? "pass" : normalized;
    }
  }

  // Anything that isn't an explicit pass is treated as a fail.
  return "fail";
}

function formatStatusValue(value, t) {
  const normalized = normaliseStatus(value);

  if (normalized === "very good") {
    return t("profilePage.statusVeryGood", "Very good");
  }

  if (normalized === "good") {
    return t("profilePage.statusGood", "Good");
  }

  if (normalized === "pass") {
    return t("profilePage.statusPass", "Pass");
  }

  if (normalized === "fail") {
    return t("profilePage.statusFail", "Fail");
  }

  if (normalized === "bad") {
    return t("profilePage.statusBad", "Bad");
  }

  return t("profilePage.statusUnknown", "Unknown");
}

function getStatusClassName(value) {
  const normalized = normaliseStatus(value);
  return (
    statusClassNames[normalized] ||
    "bg-greyscale-1 text-typography border-greyscale-1"
  );
}

function RecordingStatusPill({ value, t }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${getStatusClassName(
        value,
      )}`}
    >
      {formatStatusValue(value, t)}
    </span>
  );
}

function MeasurementRow({
  entry,
  displayIndex,
  interactive,
  selected,
  onSelect,
  t,
}) {
  const { formatDateTime } = useDateTimeFormat();
  const dateTime = formatDateTime(entry?.takenAt);
  const baseClassName = `grid w-full items-center gap-3 border-b border-greyscale-1 px-3 py-3 text-left transition-colors ${
    interactive
      ? "grid-cols-[64px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.9fr)]"
      : "grid-cols-[64px_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1fr)]"
  } ${selected ? "bg-greyscale-1" : "bg-container"}`;

  const content = (
    <>
      <span className="text-sm font-medium text-typography">
        {displayIndex}
      </span>
      {interactive ? (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-typography">
            {entry?.name || "--"}
          </p>
        </div>
      ) : null}
      <span className="text-sm text-typography">{dateTime.date || "--"}</span>
      <span className="text-sm text-typography">
        {dateTime.time || "--"}
      </span>{" "}
      <div className="flex justify-center">
        <RecordingStatusPill value={entry?.signalQualityStatus} t={t} />
      </div>
      <div className="flex justify-center">
        {entry?.spiroSignalQualityStatus ? (
          <RecordingStatusPill value={entry.spiroSignalQualityStatus} t={t} />
        ) : (
          <span className="text-sm text-greyscale-3">-</span>
        )}
      </div>
      {interactive ? (
        <>
          <span className="text-right text-sm font-medium text-primary">
            {t("profilePage.openRecording", "Open")}
          </span>
        </>
      ) : null}
    </>
  );

  if (!interactive) {
    return <div className={baseClassName}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(entry)}
      className={baseClassName}
    >
      {content}
    </button>
  );
}

function QuestionnaireTable({ title, count, entries, t, showScore = false }) {
  const { formatDateTime } = useDateTimeFormat();
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-greyscale-1 bg-container">
      <div className="patient-list-container border-b border-greyscale-1">
        <h4 className="text-base font-semibold">
          {title} ({count || 0})
        </h4>
      </div>

      <div
        className={`grid ${
          showScore ? "grid-cols-4" : "grid-cols-3"
        } gap-1 bg-greyscale-0.5 patient-list-container font-medium`}
      >
        <div>{t("profilePage.hashHeader")}</div>
        <div className="text-center">{t("profilePage.dateHeader")}</div>
        <div className="text-center">{t("profilePage.timeHeader")}</div>
        {showScore ? (
          <div className="text-center">{t("profilePage.scoreHeader")}</div>
        ) : null}
      </div>

      <div className="max-h-[320px] overflow-y-auto">
        {entries?.length ? (
          entries.map((item, index, items) => {
            const dateTime = formatDateTime(item?.takenAt);

            return (
              <div
                key={`${item.uid}-${item.name}`}
                className={`grid ${
                  showScore ? "grid-cols-4" : "grid-cols-3"
                } gap-1 patient-list-container transition-colors hover:bg-greyscale-0.5`}
              >
                <div>{items.length - index}</div>
                <div className="text-center">{dateTime.date}</div>
                <div className="text-center">{dateTime.time}</div>
                {showScore ? (
                  <div className="text-center font-medium text-primary">
                    {item.score ?? "-"}
                  </div>
                ) : null}
              </div>
            );
          })
        ) : (
          <div className="patient-list-container text-center text-greyscale-3">
            {t("profilePage.noEntries")}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PatientProfilePage({
  selectedPatient,
  setSelectedPatient,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeEntry, setActiveEntry] = useState(null);
  const [trendsOpen, setTrendsOpen] = useState(false);

  const showElectronRxProfile = isElectronRxProfileEmail(user?.email);
  const requestedSid = searchParams.get("sid")?.trim() || "";

  const effectiveSelectedPatient = useMemo(() => {
    const storedSelectedPatient = readStoredSelectedPatient();

    if (requestedSid) {
      if (selectedPatient?.sid === requestedSid) {
        return selectedPatient;
      }

      if (storedSelectedPatient?.sid === requestedSid) {
        return storedSelectedPatient;
      }

      return { sid: requestedSid };
    }

    return selectedPatient || storedSelectedPatient || null;
  }, [requestedSid, selectedPatient]);

  const emailSummary =
    patientData?.emails?.length > 1
      ? `${patientData.emails[0]} (+${patientData.emails.length - 1} more)`
      : patientData?.email;

  useEffect(() => {
    if (!effectiveSelectedPatient) {
      return;
    }

    writeStoredSelectedPatient(effectiveSelectedPatient);

    const currentSelectedId =
      selectedPatient?.sid || selectedPatient?.uid || "";
    const effectiveSelectedId =
      effectiveSelectedPatient.sid || effectiveSelectedPatient.uid || "";

    if (
      setSelectedPatient &&
      effectiveSelectedId &&
      effectiveSelectedId !== currentSelectedId
    ) {
      setSelectedPatient(effectiveSelectedPatient);
    }
  }, [effectiveSelectedPatient, selectedPatient, setSelectedPatient]);

  useEffect(() => {
    setActiveEntry(null);
    setTrendsOpen(false);
  }, [
    patientData?.sid,
    effectiveSelectedPatient?.sid,
    effectiveSelectedPatient?.uid,
    showElectronRxProfile,
  ]);

  useEffect(() => {
    if (!effectiveSelectedPatient?.sid && !effectiveSelectedPatient?.uid) {
      setPatientData(null);
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get(
          buildFunctionUrl("/aggPatientDetailFirestore"),
          {
            params: {
              sid: effectiveSelectedPatient.sid,
              uid: effectiveSelectedPatient.uid,
            },
          },
        );
        setPatientData(response.data);
      } catch (e) {
        setError(
          t("profilePage.error", {
            message:
              e.response?.data?.error ||
              e.message ||
              "Failed to load patient data",
          }),
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [effectiveSelectedPatient, t]);

  const metricCards = useMemo(
    () => [
      {
        label: t("profilePage.measurementsCount", "Measurements"),
        value: patientData?.measurements?.count || 0,
      },
      {
        label: t("profilePage.catCount", "CAT"),
        value: patientData?.questionnaires?.cat?.count || 0,
      },
      {
        label: t("profilePage.sgrqCount", "SGRQ"),
        value: patientData?.questionnaires?.sgrq?.count || 0,
      },
    ],
    [
      patientData?.measurements?.count,
      patientData?.questionnaires?.cat?.count,
      patientData?.questionnaires?.sgrq?.count,
      t,
    ],
  );

  const hasMeasurementTrendsData = Boolean(
    patientData?.measurements?.entries?.length ||
    patientData?.questionnaires?.cat?.entries?.length,
  );

  return (
    <div className="flex h-full w-full flex-col gap-4 px-4 pb-4">
      {loading ? (
        <div className="flex h-full w-full items-center justify-center">
          <p>{t("profilePage.loading")}</p>
        </div>
      ) : error ? (
        <div className="flex h-full w-full items-center justify-center">
          <p className="text-red-500">{error}</p>
        </div>
      ) : !patientData ? (
        <div className="flex h-full w-full items-center justify-center">
          <p>{t("profilePage.empty")}</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col items-start justify-start gap-1">
              <h2 className="text-xl font-semibold text-typography">
                {t("profilePage.subjectPrefix")}{" "}
                {patientData?.sid ||
                  effectiveSelectedPatient?.sid ||
                  effectiveSelectedPatient?.uid}
              </h2>
              <p className="text-sm text-greyscale-3">{emailSummary}</p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {metricCards.map((card) => (
                <div
                  key={card.label}
                  className="rounded-xl bg-background px-3 py-1 text-center"
                >
                  <p className="text-xl font-semibold text-typography">
                    {card.value}
                  </p>
                  <p className="text-sm text-greyscale-3">{card.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col h-full min-h-0 gap-4">
            <section className="flex h-full flex-col overflow-hidden rounded-2xl border border-greyscale-1">
              <div className="flex flex-col gap-3 border-b border-greyscale-1 p-2 sm:flex-row sm:items-end sm:justify-between">
                <h3 className="text-lg font-semibold">
                  {t("profilePage.measurementsTableTitle", "Measurements")}
                </h3>
                {showElectronRxProfile ? (
                  <button
                    type="button"
                    className="rounded-lg shadow-lg bg-primary !py-0.5 !px-4 !w-fit !text-greyscale-1 text-nowrap !text-sm"
                    disabled={!hasMeasurementTrendsData}
                    onClick={() => {
                      setActiveEntry(null);
                      setTrendsOpen(true);
                    }}
                  >
                    {t("profilePage.measurementTrendsButton", "Trends")}
                  </button>
                ) : null}
              </div>

              <div className="min-h-0 flex-1 overflow-x-auto overflow-y-hidden">
                <div
                  className={`flex h-full min-h-0 flex-col ${
                    showElectronRxProfile ? "min-w-[840px]" : "min-w-[560px]"
                  }`}
                >
                  <div
                    className={`grid shrink-0 gap-3 border-b border-greyscale-1 bg-greyscale-0.5 px-3 py-3 text-sm font-medium text-typography ${
                      showElectronRxProfile
                        ? "grid-cols-[64px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.9fr)]"
                        : "grid-cols-[64px_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1fr)]"
                    }`}
                  >
                    <span>{t("profilePage.hashHeader")}</span>
                    {showElectronRxProfile ? (
                      <span>
                        {t("profilePage.recordingHeader", "Recording")}
                      </span>
                    ) : null}
                    <span>{t("profilePage.dateHeader")}</span>
                    <span>{t("profilePage.timeHeader")}</span>
                    <span className="text-center">
                      {t("profilePage.signalQualityHeader", "Signal")}
                    </span>
                    <span className="text-center">
                      {t("profilePage.spirometryQualityHeader", "Spiro")}
                    </span>
                    {showElectronRxProfile ? (
                      <span className="text-right">
                        {t("profilePage.actionHeader", "Action")}
                      </span>
                    ) : null}
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto bg-container">
                    {patientData?.measurements?.entries?.length ? (
                      patientData.measurements.entries.map(
                        (entry, index, entries) => (
                          <MeasurementRow
                            key={`${entry.uid}-${entry.name}`}
                            entry={entry}
                            displayIndex={entries.length - index}
                            interactive={showElectronRxProfile}
                            selected={
                              activeEntry?.uid === entry.uid &&
                              activeEntry?.name === entry.name
                            }
                            onSelect={(selectedEntry) => {
                              setTrendsOpen(false);
                              setActiveEntry(selectedEntry);
                            }}
                            t={t}
                          />
                        ),
                      )
                    ) : (
                      <div className="px-4 py-10 text-center text-greyscale-3">
                        {t("profilePage.noEntries")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <div className="grid min-h-0 grid-cols-2 gap-4">
              <QuestionnaireTable
                title={t("profilePage.catTableTitle", "CAT Questionnaires")}
                count={patientData?.questionnaires?.cat?.count}
                entries={patientData?.questionnaires?.cat?.entries}
                showScore={true}
                t={t}
              />
              <QuestionnaireTable
                title={t("profilePage.sgrqTableTitle", "SGRQ Questionnaires")}
                count={patientData?.questionnaires?.sgrq?.count}
                entries={patientData?.questionnaires?.sgrq?.entries}
                t={t}
              />
            </div>
          </div>

          {showElectronRxProfile ? (
            <>
              <PatientRecordingOverlay
                entry={activeEntry}
                open={Boolean(activeEntry)}
                onClose={() => setActiveEntry(null)}
              />
              <PatientMeasurementTrendsOverlay
                entries={patientData?.measurements?.entries || []}
                catEntries={patientData?.questionnaires?.cat?.entries || []}
                open={trendsOpen}
                onClose={() => setTrendsOpen(false)}
              />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
